import { GetStaticProps } from 'next'
import { paths } from '@/constants'
import { Durations } from '@/constants/durations'
import { Seo } from '@/layout/Seo'
import { DefaultLayout, NextPageWithLayout } from '@/layouts'
import { getWebClient } from '@/services/ApolloClient'
import { getCollection, GUILD_MEMBERSHIP_NUMBERS_ID } from '@/services/CmsService/Collection'
import { Collection } from '@/types/codegen-contentful'
import { GetGuildMembershipCountQuery } from '@/types/codegen-federation'
import { getGenericStaticPaths } from '@/utils/GetGenericStaticPaths'
import { getLocaleFromParams } from '@/utils/LocaleUtil'
import { getStaticPropsErrorHandler, getUrlFromThisFilePath } from '@/utils/nextUtils/nextErrorHandlers'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { GET_GUILD_MEMBERSHIP_COUNT } from '@/views/GuildCreateView/queries'
import { GuildImpactView } from '@/views/GuildImpactView'

interface GuildImpactPageProps {
  locale: string
  preview: boolean
  members: number
  guildMembershipNumbers: Collection
}

const GuildImpactPage: NextPageWithLayout<GuildImpactPageProps> = ({ locale, members, guildMembershipNumbers }) => {
  const { t } = useTranslate('guild')
  const basePath = `${getBasePath(locale)}`
  const url = `${basePath}${paths.guild.impact}`
  const title = t('angelStudiosGuildImpactTitle', 'Angel Guild Impact | Angel Studios')
  const description = t(
    'angelStudiosGuildImpactMetaDescriptionV2',
    'Thousands of Guild members voted and gave feedback to help make these films a reality. Discover how these light-amplifying stories are inspiring real people and making a difference in their lives.',
  )
  return (
    <>
      <Seo
        canonical={url}
        description={description}
        openGraph={{ title, description, url }}
        path={paths.guild.impact}
        title={title}
      />
      <GuildImpactView members={members} guildMembershipNumbers={guildMembershipNumbers} />
    </>
  )
}

GuildImpactPage.getLayout = (page) => {
  return <DefaultLayout>{page}</DefaultLayout>
}

export const getStaticPaths = getGenericStaticPaths

export const getStaticProps: GetStaticProps = async ({ params, preview = false }) =>
  getStaticPropsErrorHandler({ requestUrl: getUrlFromThisFilePath(params) }, async () => {
    const locale = getLocaleFromParams(params)
    const client = getWebClient()

    const { data: members } = await client.query<GetGuildMembershipCountQuery>({
      query: GET_GUILD_MEMBERSHIP_COUNT,
      errorPolicy: 'all',
    })

    const guildMembershipNumbers = await getCollection({ locale, preview }, GUILD_MEMBERSHIP_NUMBERS_ID)

    return {
      props: {
        locale,
        preview,
        members: members?.guildMembershipCount,
        guildMembershipNumbers,
        ...(await loadTranslations(locale, ['common', 'guild', 'watch'])),
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  })

export default GuildImpactPage
